import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Plus,
  Eye,
  Edit,
  Trash2,
  Users,
  TrendingUp,
  Sparkles,
  Settings,
  Phone,
} from "lucide-react";
import { Button } from "../../components";
import {
  SearchAndFilter,
  DataCard,
  EmptyState,
} from "../../components/shared";
import { LoadingState } from "@/components/ui/loading-state";
import { PageHeader } from "@/components/ui/page-header";
import { PageIntent } from "../../docs/ux/page-intents";

// Page Intent: MONITORING - operational monitoring, no stats cards, allow alert-style counters only
const pageIntent: PageIntent = "MONITORING";

interface ContactCenter {
  id: string;
  name: string;
  description: string;
  status: "ACTIVE" | "INACTIVE" | "MAINTENANCE";
  type: "INBOUND" | "OUTBOUND" | "BLENDED";
  agents: number;
  queues: number;
  avgResponseTime: number;
  satisfactionScore: number;
  totalCalls: number;
  activeCalls: number;
  waitTime: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  tags: string[];
  features: string[];
  routingRules: number;
  ivrMenus: number;
}

const ContactCenterPage: React.FC = () => {
  const { t } = useTranslation("common");
  const { t: tCommon } = useTranslation("common");
  const [contactCenters, setContactCenters] = useState<ContactCenter[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState<string>("all");
  const [selectedType, setSelectedType] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState("");

  // Mock data - replace with actual API calls
  useEffect(() => {
    const mockContactCenters: ContactCenter[] = [
      {
        id: "1",
        name: "Main Support Center",
        description:
          "Primary customer support contact center for all inquiries",
        status: "ACTIVE",
        type: "INBOUND",
        agents: 25,
        queues: 8,
        avgResponseTime: 45,
        satisfactionScore: 4.8,
        totalCalls: 1250,
        activeCalls: 12,
        waitTime: 2.5,
        createdBy: "John Admin",
        createdAt: "2024-01-10T08:00:00Z",
        updatedAt: "2024-01-15T10:00:00Z",
        tags: ["support", "customer-service"],
        features: ["IVR", "Call Recording", "Analytics", "CRM Integration"],
        routingRules: 15,
        ivrMenus: 5,
      },
      {
        id: "2",
        name: "Sales Contact Center",
        description: "Outbound sales and lead generation center",
        status: "ACTIVE",
        type: "OUTBOUND",
        agents: 15,
        queues: 4,
        avgResponseTime: 30,
        satisfactionScore: 4.6,
        totalCalls: 890,
        activeCalls: 8,
        waitTime: 1.8,
        createdBy: "Sarah Manager",
        createdAt: "2024-01-08T09:00:00Z",
        updatedAt: "2024-01-14T14:00:00Z",
        tags: ["sales", "outbound"],
        features: ["Predictive Dialer", "Lead Management", "Call Scripts"],
        routingRules: 8,
        ivrMenus: 2,
      },
      {
        id: "3",
        name: "Technical Support",
        description: "Specialized technical support for complex issues",
        status: "ACTIVE",
        type: "BLENDED",
        agents: 12,
        queues: 6,
        avgResponseTime: 120,
        satisfactionScore: 4.9,
        totalCalls: 567,
        activeCalls: 5,
        waitTime: 5.2,
        createdBy: "Mike Support",
        createdAt: "2024-01-05T11:00:00Z",
        updatedAt: "2024-01-15T09:00:00Z",
        tags: ["technical", "escalation"],
        features: ["Screen Sharing", "Remote Support", "Knowledge Base"],
        routingRules: 12,
        ivrMenus: 3,
      },
      {
        id: "4",
        name: "Emergency Response",
        description: "24/7 emergency response and critical support",
        status: "ACTIVE",
        type: "INBOUND",
        agents: 8,
        queues: 3,
        avgResponseTime: 15,
        satisfactionScore: 4.7,
        totalCalls: 234,
        activeCalls: 2,
        waitTime: 0.5,
        createdBy: "Lisa Admin",
        createdAt: "2024-01-03T10:00:00Z",
        updatedAt: "2024-01-15T08:00:00Z",
        tags: ["emergency", "critical"],
        features: ["Priority Routing", "Escalation", "Alert System"],
        routingRules: 6,
        ivrMenus: 2,
      },
      {
        id: "5",
        name: "Billing Support",
        description: "Billing and payment related inquiries",
        status: "INACTIVE",
        type: "INBOUND",
        agents: 10,
        queues: 4,
        avgResponseTime: 60,
        satisfactionScore: 4.5,
        totalCalls: 0,
        activeCalls: 0,
        waitTime: 0,
        createdBy: "Alex Finance",
        createdAt: "2024-01-12T14:00:00Z",
        updatedAt: "2024-01-15T16:00:00Z",
        tags: ["billing", "payments"],
        features: [
          "Payment Processing",
          "Invoice Management",
          "Refund Handling",
        ],
        routingRules: 8,
        ivrMenus: 3,
      },
    ];

    setContactCenters(mockContactCenters);
    setLoading(false);
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "ACTIVE":
        return "success";
      case "INACTIVE":
        return "gray";
      case "MAINTENANCE":
        return "warning";
      default:
        return "gray";
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case "INBOUND":
        return "info";
      case "OUTBOUND":
        return "warning";
      case "BLENDED":
        return "info";
      default:
        return "gray";
    }
  };

  const filteredContactCenters = contactCenters.filter(center => {
    const matchesStatus =
      selectedStatus === "all" || center.status === selectedStatus;
    const matchesType = selectedType === "all" || center.type === selectedType;
    const matchesSearch =
      center.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      center.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      center.tags.some(tag =>
        tag.toLowerCase().includes(searchTerm.toLowerCase()),
      );

    return matchesStatus && matchesType && matchesSearch;
  });

  if (loading) {
    return <LoadingState />;
  }

  const filterOptions = [
    {
      label: tCommon("all-statuses") || "All Statuses",
      value: "all",
    },
    {
      label: tCommon("active") || "Active",
      value: "ACTIVE",
    },
    {
      label: tCommon("inactive") || "Inactive",
      value: "INACTIVE",
    },
    {
      label: tCommon("maintenance") || "Maintenance",
      value: "MAINTENANCE",
    },
  ];

  const typeOptions = [
    {
      label: tCommon("all-types") || "All Types",
      value: "all",
    },
    {
      label: tCommon("inbound") || "Inbound",
      value: "INBOUND",
    },
    {
      label: tCommon("outbound") || "Outbound",
      value: "OUTBOUND",
    },
    {
      label: tCommon("blended") || "Blended",
      value: "BLENDED",
    },
  ];

  return (
    <div className="min-h-full bg-white">
      <PageHeader
        title={tCommon("contactCenter") || "Contact Center"}
        description={
          tCommon("contact-center-description") ||
          "Manage your contact center operations"
        }
        icon={Phone}
        iconBgColor="from-blue-500 to-blue-600"
        actions={
          <Button color="blue" size="sm">
            <Plus className="mr-2 h-4 w-4" />
            {t("create-center")}
          </Button>
        }
      />

      <div className="mx-auto px-4 py-8 sm:px-6 lg:px-8">
        <SearchAndFilter
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder={
            tCommon("search-for-center") || "Search for center..."
          }
          filters={[
            {
              label: tCommon("status") || "Status",
              value: selectedStatus,
              options: filterOptions,
              onChange: setSelectedStatus,
            },
            {
              label: tCommon("type") || "Type",
              value: selectedType,
              options: typeOptions,
              onChange: setSelectedType,
            },
          ]}
          resultsCount={filteredContactCenters.length}
          totalCount={contactCenters.length}
          resultsLabel={tCommon("centers") || "centers"}
        />

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filteredContactCenters.map(center => (
            <DataCard
              key={center.id}
              id={center.id}
              title={center.name}
              description={center.description}
              icon={Phone}
              iconBgColor="from-blue-500 to-blue-600"
              status={{
                label: t(center.status.toLowerCase()),
                color: getStatusColor(center.status),
              }}
              badges={[
                {
                  label: t(center.type.toLowerCase()),
                  color: getTypeColor(center.type),
                },
                {
                  label: `${center.agents} ${tCommon("agents") || "agents"}`,
                  color: "info",
                },
              ]}
              stats={[
                {
                  label: tCommon("active-calls") || "Active Calls",
                  value: center.activeCalls,
                },
                {
                  label: tCommon("avg-response-time") || "Avg Response Time",
                  value: `${center.avgResponseTime}s`,
                },
                {
                  label: tCommon("satisfaction") || "Satisfaction",
                  value: center.satisfactionScore.toFixed(1),
                  color: "text-green-600",
                },
                {
                  label: tCommon("avg-wait-time") || "Wait Time",
                  value: `${center.waitTime}min`,
                },
              ]}
              tags={center.tags}
              metadata={[
                {
                  label: tCommon("queues") || "Queues",
                  value: center.queues.toString(),
                },
                {
                  label: tCommon("created-by") || "Created By",
                  value: center.createdBy,
                },
              ]}
              actions={[
                {
                  label: tCommon("view") || "View",
                  icon: Eye,
                  onClick: () => console.log("View center", center.id),
                },
                {
                  label: tCommon("edit") || "Edit",
                  icon: Edit,
                  onClick: () => console.log("Edit center", center.id),
                },
                {
                  label: tCommon("configure") || "Configure",
                  icon: Settings,
                  onClick: () => console.log("Configure center", center.id),
                },
                {
                  label: tCommon("delete") || "Delete",
                  icon: Trash2,
                  color: "red",
                  onClick: () => console.log("Delete center", center.id),
                },
              ]}
            />
          ))}
        </div>

        {filteredContactCenters.length === 0 && (
          <EmptyState
            icon={Phone}
            title={tCommon("no-centers-found") || "No centers found"}
            description={
              tCommon("no-centers-description") ||
              "Create your first contact center to get started"
            }
            action={
              <Button color="blue" size="sm">
                <Plus className="mr-2 h-4 w-4" />
                {tCommon("create-center") || "Create Center"}
              </Button>
            }
          />
        )}
      </div>
    </div>
  );
};

export default ContactCenterPage;
